// Inputs for the richer question types: matrix, slider, NPS, constant sum,
// text form, file upload, signature and location. Values follow the shared
// model in @survey/core (see AnswerValue).
import { DEFAULT_TOTAL, effectiveScale, MAX_SIGNATURE_LENGTH, MAX_UPLOAD_BYTES, optionOrder, rowOrder, type AnswerRecord, type AnswerValue, type Question } from '@survey/core';
import { Eraser, FileUp, Loader2, LocateFixed, MapPin, Paperclip, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FileAnswer } from '../api/client';
import { formatBytes } from '../format';
import { useI18n } from '../i18n';

interface InputProps {
  question: Question;
  value: AnswerValue | undefined;
  onChange: (v: AnswerValue) => void;
  seed: string;
  invalid?: boolean;
}

const asRecord = (v: AnswerValue | undefined): AnswerRecord => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});

/** Drops empty entries so an untouched record counts as unanswered. */
function compact(r: AnswerRecord): AnswerRecord | null {
  const out: AnswerRecord = {};
  for (const [k, v] of Object.entries(r)) if (v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)) out[k] = v;
  return Object.keys(out).length ? out : null;
}

export function MatrixInput({ question: q, value, onChange, seed, invalid }: InputProps) {
  const rows = useMemo(() => rowOrder(q, seed), [q, seed]);
  const cols = useMemo(() => optionOrder(q, seed), [q, seed]);
  const answer = asRecord(value);
  const multi = !!q.matrixMultiple;
  const set = (rowId: string, colId: string) => {
    const cell = answer[rowId];
    let next: AnswerRecord[string];
    if (multi) {
      const cur = Array.isArray(cell) ? cell : [];
      next = cur.includes(colId) ? cur.filter((x) => x !== colId) : [...cur, colId];
    } else next = colId;
    onChange(compact({ ...answer, [rowId]: next }));
  };
  return (
    <div className={`matrix ${invalid ? 'invalid' : ''}`}>
      <table className="matrix-table">
        <thead>
          <tr>
            <th aria-hidden />
            {cols.map((c) => (
              <th key={c.id} scope="col">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const cell = answer[r.id];
            const done = Array.isArray(cell) ? cell.length > 0 : !!cell;
            return (
              <tr key={r.id} className={done ? 'done' : undefined} role={multi ? 'group' : 'radiogroup'} aria-label={r.label}>
                <th scope="row">{r.label}</th>
                {cols.map((c) => {
                  const on = Array.isArray(cell) ? cell.includes(c.id) : cell === c.id;
                  return (
                    <td key={c.id}>
                      <label className={`matrix-cell ${on ? 'selected' : ''}`}>
                        <input type={multi ? 'checkbox' : 'radio'} name={`q-${q.id}-${r.id}`} checked={on} onChange={() => set(r.id, c.id)} aria-label={`${r.label}: ${c.label}`} />
                        <span className={`mark ${multi ? '' : 'radio'}`} aria-hidden />
                        <span className="matrix-col-label">{c.label}</span>
                      </label>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function SliderInput({ question: q, value, onChange, invalid }: InputProps) {
  const { t } = useI18n();
  const s = effectiveScale(q);
  const step = s.step ?? 1;
  const set = typeof value === 'number';
  const mid = s.min + Math.round((s.max - s.min) / 2 / step) * step;
  return (
    <div className={`slider ${set ? '' : 'unset'} ${invalid ? 'invalid' : ''}`}>
      <div className="slider-value" aria-hidden>
        {set ? value : t('input.slider_unset')}
      </div>
      <input
        id={`q-${q.id}`}
        type="range"
        min={s.min}
        max={s.max}
        step={step}
        value={set ? (value as number) : mid}
        onChange={(e) => onChange(Number(e.target.value))}
        onClick={(e) => !set && onChange(Number((e.target as HTMLInputElement).value))}
        aria-valuetext={set ? String(value) : t('input.slider_unset')}
      />
      <div className="row small muted">
        <span>{s.minLabel || s.min}</span>
        <span className="grow" />
        <span>{s.maxLabel || s.max}</span>
      </div>
    </div>
  );
}

export function NpsInput({ question: q, value, onChange }: InputProps) {
  const { t } = useI18n();
  const s = effectiveScale(q);
  return (
    <div>
      <div className="rating nps" role="radiogroup" aria-labelledby={`q-${q.id}-title`}>
        {Array.from({ length: 11 }, (_, n) => (
          <label key={n} className={`rating-pt ${value === n ? 'selected' : ''}`}>
            <input type="radio" name={`q-${q.id}`} checked={value === n} onChange={() => onChange(n)} />
            {n}
          </label>
        ))}
      </div>
      <div className="row small muted mt-8">
        <span>{s.minLabel || t('input.nps_low')}</span>
        <span className="grow" />
        <span>{s.maxLabel || t('input.nps_high')}</span>
      </div>
    </div>
  );
}

export function ConstantSumInput({ question: q, value, onChange, seed, invalid }: InputProps) {
  const { t } = useI18n();
  const items = useMemo(() => optionOrder(q, seed), [q, seed]);
  const answer = asRecord(value);
  const total = q.total ?? DEFAULT_TOTAL;
  const sum = Object.values(answer).reduce<number>((n, v) => n + (typeof v === 'number' ? v : 0), 0);
  const left = Math.round((total - sum) * 1000) / 1000;
  return (
    <div className={`stack gap-8 ${invalid ? 'invalid' : ''}`}>
      {items.map((o) => (
        <label key={o.id} className="sum-row">
          <span className="grow">{o.label}</span>
          <input
            className="input"
            type="number"
            inputMode="decimal"
            min={0}
            max={total}
            step={q.validation?.integer ? 1 : 'any'}
            value={typeof answer[o.id] === 'number' ? (answer[o.id] as number) : ''}
            onChange={(e) => onChange(compact({ ...answer, [o.id]: e.target.value === '' ? null : Number(e.target.value) }))}
          />
        </label>
      ))}
      <div className={`sum-total ${left === 0 ? 'ok' : left < 0 ? 'over' : ''}`} aria-live="polite">
        <span>{t('input.sum_total', { sum: Math.round(sum * 1000) / 1000, total })}</span>
        <span>{left === 0 ? '✓' : left > 0 ? t('input.sum_left', { n: left }) : t('input.sum_over', { n: -left })}</span>
      </div>
    </div>
  );
}

export function TextFormInput({ question: q, value, onChange, invalid }: InputProps) {
  const answer = asRecord(value);
  return (
    <div className="form-grid">
      {(q.options ?? []).map((f, i) => (
        <label key={f.id} className="field">
          <span className="label">{f.label}</span>
          <input
            id={i === 0 ? `q-${q.id}` : undefined}
            className={`input ${invalid && !answer[f.id] ? 'invalid' : ''}`}
            value={typeof answer[f.id] === 'string' ? (answer[f.id] as string) : ''}
            maxLength={q.validation?.maxLength ?? 10000}
            onChange={(e) => onChange(compact({ ...answer, [f.id]: e.target.value }))}
          />
        </label>
      ))}
    </div>
  );
}

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/heic,.heic,application/pdf,.docx,.xlsx,.pptx,.csv,.txt';

export function FileInput({ question: q, value, onChange, uploader }: InputProps & { uploader?: (file: File) => Promise<FileAnswer> }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  const file = asRecord(value);
  const pick = async (f: File | undefined) => {
    if (!f) return;
    setError(null);
    if (f.size > MAX_UPLOAD_BYTES) {
      setError(t('input.file_too_big', { mb: MAX_UPLOAD_BYTES >> 20 }));
      return;
    }
    if (!uploader) return;
    setBusy(true);
    try {
      onChange({ ...(await uploader(f)) });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('input.file_failed'));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = '';
    }
  };
  return (
    <div className="stack gap-8">
      <input ref={ref} id={`q-${q.id}`} type="file" accept={ACCEPT} hidden onChange={(e) => void pick(e.target.files?.[0])} />
      {typeof file.name === 'string' ? (
        <div className="file-chip">
          <Paperclip aria-hidden />
          <span className="grow ellipsis">{file.name}</span>
          <span className="small muted">{formatBytes(Number(file.size) || 0)}</span>
          <button type="button" className="btn ghost sm" onClick={() => ref.current?.click()} disabled={busy}>
            {t('input.file_replace')}
          </button>
          <button type="button" className="btn ghost sm icon" aria-label={t('input.file_remove')} onClick={() => onChange(null)} disabled={busy}>
            <X />
          </button>
        </div>
      ) : (
        <button type="button" className="btn file-drop" onClick={() => ref.current?.click()} disabled={busy}>
          {busy ? <Loader2 className="spin" /> : <FileUp />} {busy ? t('input.file_uploading') : t('input.file_choose')}
        </button>
      )}
      <p className="hint">{t('input.file_hint', { mb: MAX_UPLOAD_BYTES >> 20 })}</p>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** Draws with mouse, pen or finger; stores a PNG data URL when a stroke ends. */
export function SignatureInput({ question: q, value, onChange, invalid }: InputProps) {
  const { t } = useI18n();
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);

  // Size the backing store to the element and redraw a saved signature (resumed sessions).
  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.clientWidth * ratio;
    c.height = c.clientHeight * ratio;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    if (typeof value === 'string' && value.startsWith('data:image/png')) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, c.clientWidth, c.clientHeight);
      img.src = value;
    }
    // Only on mount; later strokes are drawn directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const line = (to: { x: number; y: number }) => {
    const ctx = canvas.current?.getContext('2d');
    if (!ctx) return;
    const from = last.current ?? to;
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x + (from === to ? 0.01 : 0), to.y);
    ctx.stroke();
    last.current = to;
  };
  const save = () => {
    const c = canvas.current;
    if (!c) return;
    let url = c.toDataURL('image/png');
    if (url.length > MAX_SIGNATURE_LENGTH) {
      // Very detailed drawings on high-density screens: store at screen resolution.
      const small = document.createElement('canvas');
      small.width = c.clientWidth;
      small.height = c.clientHeight;
      small.getContext('2d')?.drawImage(c, 0, 0, small.width, small.height);
      url = small.toDataURL('image/png');
    }
    onChange(url);
  };
  const clear = () => {
    const c = canvas.current;
    c?.getContext('2d')?.clearRect(0, 0, c.width, c.height);
    onChange(null);
  };
  return (
    <div className="stack gap-8">
      <canvas
        ref={canvas}
        id={`q-${q.id}`}
        className={`signature-pad ${invalid ? 'invalid' : ''}`}
        role="img"
        aria-label={t('input.sign_here')}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          drawing.current = true;
          last.current = null;
          line(point(e));
        }}
        onPointerMove={(e) => drawing.current && line(point(e))}
        onPointerUp={() => {
          if (!drawing.current) return;
          drawing.current = false;
          save();
        }}
      />
      <div className="row small muted">
        <span>{value ? t('input.signed') : t('input.sign_here')}</span>
        <span className="grow" />
        <button type="button" className="btn ghost sm" onClick={clear} disabled={!value}>
          <Eraser /> {t('input.sign_clear')}
        </button>
      </div>
    </div>
  );
}

export function LocationInput({ question: q, value, onChange }: InputProps) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loc = asRecord(value);
  const has = typeof loc.lat === 'number' && typeof loc.lng === 'number';
  const locate = () => {
    if (!navigator.geolocation) {
      setError(t('input.location_unavailable'));
      return;
    }
    setBusy(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setBusy(false);
        onChange({ lat: round(p.coords.latitude, 6), lng: round(p.coords.longitude, 6), accuracy: Math.round(p.coords.accuracy) });
      },
      (e) => {
        setBusy(false);
        setError(e.code === e.PERMISSION_DENIED ? t('input.location_denied') : t('input.location_unavailable'));
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 },
    );
  };
  return (
    <div className="stack gap-8">
      {has && (
        <div className="file-chip">
          <MapPin aria-hidden />
          <span className="grow">
            {(loc.lat as number).toFixed(5)}, {(loc.lng as number).toFixed(5)}
            {typeof loc.accuracy === 'number' && <span className="muted"> · {t('input.location_accuracy', { m: loc.accuracy })}</span>}
          </span>
          <a className="btn ghost sm" href={`https://www.openstreetmap.org/?mlat=${loc.lat}&mlon=${loc.lng}#map=17/${loc.lat}/${loc.lng}`} target="_blank" rel="noreferrer">
            {t('input.location_view')}
          </a>
          <button type="button" className="btn ghost sm icon" aria-label={t('input.location_clear')} onClick={() => onChange(null)}>
            <X />
          </button>
        </div>
      )}
      <button id={`q-${q.id}`} type="button" className="btn" onClick={locate} disabled={busy} style={{ alignSelf: 'flex-start' }}>
        {busy ? <Loader2 className="spin" /> : <LocateFixed />} {busy ? t('input.location_locating') : has ? t('input.location_again') : t('input.location_share')}
      </button>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function round(n: number, digits: number) {
  const f = 10 ** digits;
  return Math.round(n * f) / f;
}
